import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import "./HustleEatsPage.css";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
/* HustleEats — public "fill the empty seat" discovery page.
   See /app/memory/HUSTLEEATS_PLAYBOOK.md.
   This is Phase 1 MVP: diners see live offers, pick one, claim it. */

const DEAL_TYPE_LABELS = {
  early_bird: "Early sitting",
  peak: "Prime time",
  late_dining: "Late dining",
  event_day: "Event day",
};

const DAY_LABELS = ["", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function moneyFromCents(cents) {
  const dollars = (cents || 0) / 100;
  return dollars.toFixed(dollars % 1 === 0 ? 0 : 2);
}

function OfferCard({ offer, onClaim, claiming }) {
  const days = (offer.days_of_week || []).map((d) => DAY_LABELS[d]).join(" · ") || "Any day";
  const remaining = offer.max_covers - (offer.claimed_covers || 0);
  const soldOut = remaining <= 0;

  return (
    <article className="he-card" data-testid={`he-offer-card-${offer.id}`}>
      {offer.hero_image_url && (
        <div className="he-card__media" style={{ backgroundImage: `url(${offer.hero_image_url})` }} />
      )}
      <div className="he-card__body">
        <div className="he-card__chips">
          <span className="he-chip he-chip--deal">{DEAL_TYPE_LABELS[offer.deal_type] || offer.deal_type}</span>
          {soldOut && <span className="he-chip he-chip--soldout">Filled</span>}
        </div>
        <h3 className="he-card__title">{offer.title}</h3>
        <p className="he-card__blurb">{offer.blurb}</p>
        <dl className="he-card__meta">
          <div>
            <dt>Thank-you credit</dt>
            <dd className="he-card__credit">${moneyFromCents(offer.credit_amount_cents)}</dd>
          </div>
          <div>
            <dt>When</dt>
            <dd>{days}{offer.time_start ? ` · ${offer.time_start}–${offer.time_end}` : ""}</dd>
          </div>
          <div>
            <dt>Seats left</dt>
            <dd>
              <strong>{Math.max(remaining, 0)}</strong>
              <span className="he-card__cap"> / {offer.max_covers}</span>
            </dd>
          </div>
        </dl>
        <button
          type="button"
          className="he-card__cta"
          data-testid={`he-claim-btn-${offer.id}`}
          onClick={() => onClaim(offer)}
          disabled={soldOut || claiming === offer.id}
        >
          {claiming === offer.id ? "Reserving…" : soldOut ? "Filled" : "Reserve my seat"}
        </button>
      </div>
    </article>
  );
}

function ClaimSuccessModal({ claim, onClose }) {
  if (!claim) return null;
  return (
    <div className="he-modal__backdrop" onClick={onClose} data-testid="he-claim-modal">
      <div className="he-modal" onClick={(e) => e.stopPropagation()}>
        <div className="he-modal__eyebrow">Seat reserved · show this on arrival</div>
        <div className="he-modal__code" data-testid="he-claim-code">
          {claim.redemption_code}
        </div>
        <p className="he-modal__note">
          Walk in, give your name and this 6-letter code to staff. They&apos;ll mark you as seated, and
          your <strong>${moneyFromCents(claim.credit_amount_cents)}</strong> thank-you credit will
          land in your HustleEats wallet for your next visit.
        </p>
        <p className="he-modal__expiry">
          Code valid until {new Date(claim.expires_at).toLocaleString("en-AU", { timeZone: "Australia/Brisbane" })}
        </p>
        <button type="button" className="he-modal__close" onClick={onClose} data-testid="he-claim-close">
          Got it
        </button>
      </div>
    </div>
  );
}

export default function HustleEatsPage() {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [filter, setFilter] = useState("all");
  const [claiming, setClaiming] = useState("");
  const [claim, setClaim] = useState(null);
  const [authNote, setAuthNote] = useState("");

  const fetchOffers = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/hustleeats/offers?live_now=true&limit=50`);
      const data = await res.json();
      setOffers(Array.isArray(data) ? data : []);
      setErrorMsg("");
    } catch (e) {
      setErrorMsg("Couldn't load offers — please refresh in a moment.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOffers();
  }, []);

  const filtered = offers.filter((o) => filter === "all" || o.deal_type === filter);

  const handleClaim = async (offer) => {
    setClaiming(offer.id);
    setAuthNote("");
    try {
      const res = await fetch(`${API}/api/hustleeats/offers/${offer.id}/claim`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
      if (res.status === 401) {
        setAuthNote("Sign in or create your free HustleEats account to reserve this seat.");
        return;
      }
      const data = await res.json();
      if (!res.ok) {
        setAuthNote(data.detail || "Couldn't reserve that seat — it may have filled while you tapped.");
        return;
      }
      setClaim(data);
      fetchOffers();
    } catch (e) {
      setAuthNote("Network error — please try again.");
    } finally {
      setClaiming("");
    }
  };

  return (
    <div className="he-page">
      <header className="he-hero">
        <div className="he-hero__inner">
          <span className="he-eyebrow">HustleEats · Brisbane</span>
          <h1 className="he-h1">Fill an empty seat. Earn a thank-you credit.</h1>
          <p className="he-lede">
            No discount-chasing. No bargain-bin food. Pay the full menu price and walk out with
            a thank-you credit dropped in your wallet for your next visit. Restaurants keep their
            margin, you get rewarded for showing up — and your favourite places stay open.
          </p>
          <div className="he-hero__actions">
            <Link to="/eat/wallet" className="he-hero__link" data-testid="he-link-wallet">My wallet</Link>
            <Link to="/eat/how" className="he-hero__link" data-testid="he-link-how">How it works</Link>
          </div>
        </div>
      </header>

      <section className="he-filterbar">
        {[
          ["all", "All offers"],
          ["early_bird", "Early sittings"],
          ["peak", "Prime time"],
          ["late_dining", "Late dining"],
          ["event_day", "Event days"],
        ].map(([k, label]) => (
          <button
            key={k}
            type="button"
            className={`he-filter ${filter === k ? "is-active" : ""}`}
            onClick={() => setFilter(k)}
            data-testid={`he-filter-${k}`}
          >
            {label}
          </button>
        ))}
      </section>

      {authNote && (
        <div className="he-banner" data-testid="he-banner">
          {authNote}
        </div>
      )}

      <main className="he-grid">
        {loading && <div className="he-state">Loading offers…</div>}
        {!loading && filtered.length === 0 && !errorMsg && (
          <div className="he-state" data-testid="he-empty">
            No live offers right now. Check back closer to dinner — restaurants typically post 1–4 hours ahead.
          </div>
        )}
        {errorMsg && <div className="he-state he-state--err">{errorMsg}</div>}
        {filtered.map((offer) => (
          <OfferCard
            key={offer.id}
            offer={offer}
            onClaim={handleClaim}
            claiming={claiming}
          />
        ))}
      </main>

      {/* Events Booking System promo — added Feb 2026 */}
      <section
        className="bg-gradient-to-br from-amber-50 via-rose-50 to-rose-100 px-6 py-12 mt-8 border-t border-amber-200"
        data-testid="he-events-promo"
      >
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
          <div>
            <div className="text-xs uppercase tracking-wider text-amber-700 font-semibold mb-2">
              New · Event Booking System
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mb-3">
              Wine tastings · Trivia nights · Tribute bands
            </h2>
            <p className="text-slate-700 mb-5 max-w-xl">
              Browse upcoming in-venue events from restaurants and bars across the network. Reserve your
              seat in seconds — some events take a small deposit to hold your spot. Owners can publish recurring events
              (with smart Aussie public-holiday detection) right from their admin.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/eats/events"
                data-testid="he-events-cta-browse"
                className="px-5 py-3 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800"
              >
                Browse events →
              </Link>
              <Link
                to="/admin/eats/events"
                data-testid="he-events-cta-host"
                className="px-5 py-3 rounded-lg bg-white border border-slate-300 text-slate-900 text-sm font-semibold hover:bg-slate-50"
              >
                Host an event
              </Link>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2 text-center">
            {[
              ["🍷", "Wine"],
              ["🧠", "Trivia"],
              ["🎸", "Tribute"],
              ["🍽️", "Tasting"],
              ["🎱", "Bingo"],
              ["🎭", "Comedy"],
            ].map(([emoji, label]) => (
              <div key={label} className="bg-white rounded-xl p-4 shadow-sm">
                <div className="text-3xl mb-1">{emoji}</div>
                <div className="text-xs font-medium text-slate-700">{label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ClaimSuccessModal claim={claim} onClose={() => setClaim(null)} />
    </div>
  );
}
